'use client'

import React, { FC, ReactElement } from 'react'
import { ChevronLeft } from '@mui/icons-material'
import classNames from 'classnames'
import Tabs from '@/components/tabs'
import Modal from '@/components/modal'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import CollectionIcon from '@/components/collection-icon'
import isRune from '@/utils/is-rune'

import Brc20Transfer from './brc20-transfer'
import Brc20List from './brc20-list'
import RuneTransfer from './rune-transfer'
import RuneList from './rune-list'
import RuneEtch from './rune-etch'
import RuneMint from './rune-mint'

import formatNumber from '@/utils/format-number'
import Skeleton from '@/components/skeleton'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const BRC20Manager: FC<{
	onComplete: () => void
	toggleOpen: () => void
	open: boolean
	tab: string
	ticker: string
	tickerType?: string
	slug?: string
	setTab: (id: string) => void
}> = (props): ReactElement => {
	const { slug, tab, setTab, toggleOpen, open, onComplete, ticker, tickerType } = props
	const { isMobile } = useWindowSize()

	const { data: brc20Data } = useBrc20Balance()

	const balance = React.useMemo(
		() => (brc20Data || []).find((e) => e.ticker === ticker),
		[brc20Data, ticker]
	)

	const tabs = React.useMemo(() => {
		let t = [
			{
				title: 'List for sale',
				onClick: () => setTab('list'),
				selected: tab === 'list'
			},
			{
				title: tickerType === 'rune' ? 'Transfer or Split' : 'Inscribe transfer',
				onClick: () => setTab('transfer'),
				selected: tab === 'transfer'
			}
		]

		return t
	}, [tickerType, tab, setTab])

	const bodyEl = (
		<div
			className={classNames('ease-in-out duration-300 transition-[height] gap-6 flex flex-col', {
				['min-h-full h-fit']: open,
				['h-0']: !open
			})}
		>
			{!['etch', 'mint'].includes(tab) && <Tabs tabs={tabs} containerClassName="w-full p-0" />}
			{!['etch', 'mint'].includes(tab) && balance?.ticker && (
				<div className="bg-card py-3 px-4 rounded-xl flex gap-3">
					<CollectionIcon
						collection={balance?.collection}
						imageClassName="h-[38px] w-[38px] min-w-[38px] rounded-full"
						className="size-[38px] min-w-[38px] rounded-full overflow-hidden"
					/>
					<div className="flex flex-col w-full justify-center">
						<div className="flex text-sm font-bold justify-between">
							<p>{balance?.ticker}</p>
							<p>
								{formatNumber(balance?.overall_balance)}
								{isRune(slug as any) && ` ${balance?.symbol}`}
							</p>
						</div>
						{tickerType !== 'rune' && (
							<div className="flex text-xs text-muted-foreground justify-between">
								<p>Transferable</p>
								<p>{formatNumber(balance?.transferable_balance)}</p>
							</div>
						)}
					</div>
				</div>
			)}
			{!['etch', 'mint'].includes(tab) && !balance?.ticker && (
				<div className="bg-card py-3 px-4 rounded-xl flex gap-3">
					<div className="bg-foreground/10 size-[38px] min-w-[38px] rounded-full" />
					<div className="flex flex-col w-full">
						<div className="flex text-sm font-bold justify-between">
							<div className="h-[20px] flex items-center">
								<Skeleton className="w-20 h-[16px]" />
							</div>
							<div className="h-[20px] flex items-center">
								<Skeleton className="w-12 h-[16px]" />
							</div>
						</div>
						<div className="flex text-xs text-muted-foreground justify-between">
							<p>Transferable</p>
							<div className="h-[16px] flex items-center">
								<Skeleton className="w-12 h-[12px]" />
							</div>
						</div>
					</div>
				</div>
			)}
			{tickerType == 'rune' && (
				<>
					{tab === 'transfer' && (
						<RuneTransfer ticker={ticker} balance={balance} onComplete={onComplete} />
					)}
					{tab === 'mint' && <RuneMint ticker={ticker} balance={balance} onComplete={onComplete} />}
					{tab === 'list' && (
						<RuneList balance={balance} ticker={ticker} onComplete={onComplete} setTab={setTab} />
					)}
					{tab === 'etch' && <RuneEtch onComplete={onComplete} />}
				</>
			)}
			{tickerType != 'rune' && (
				<>
					{tab === 'transfer' && (
						<Brc20Transfer ticker={ticker} balance={balance} onComplete={onComplete} />
					)}
					{tab === 'list' && (
						<Brc20List ticker={ticker} onComplete={onComplete} setTab={setTab} slug={slug} />
					)}
				</>
			)}
		</div>
	)

	const headerEl = (
		<div
			onClick={toggleOpen}
			className="flex items-center justify-between h-[66px] cursor-pointer"
			role="button"
			tabIndex={0}
			onKeyDown={onKeyboardClick(toggleOpen)}
		>
			{tab !== 'etch' && (
				<p className="text-base font-semibold sm:text-foreground text-warning dark:text-[#FFF500] select-none">
					{ticker}
				</p>
			)}
			{tab === 'etch' && (
				<p className="text-base font-semibold sm:text-foreground text-warning dark:text-[#FFF500] select-none">
					Etch Rune
				</p>
			)}
			<ChevronLeft
				className={classNames('size-6 fill-foreground', {
					['rotate-90']: open,
					['-rotate-90']: !open
				})}
			/>
		</div>
	)

	return (
		<div className="fixed bottom-0 right-0 sm:right-12 w-full sm:w-[400px] z-[101]">
			<div className="w-full flex justify-end">
				<div className="gap-5 pt-2 pb-8 w-full sm:w-[400px] bg-background sm:border-x border-t border-border px-5 sm:rounded-t-lg z-[101] max-h-[calc(100vh-148px)] overflow-auto">
					{headerEl}
					{!isMobile && bodyEl}
					{isMobile && (
						<Modal open={open} onClose={toggleOpen}>
							<div className="px-4">{headerEl}</div>
							<div className="max-h-[calc(100vh-130px)] overflow-auto p-4">{bodyEl}</div>
						</Modal>
					)}
				</div>
			</div>
		</div>
	)
}

export default BRC20Manager
